import { useCallback, useEffect, useRef, useState } from 'react'

import { EventAdminService } from '../services/Event/EventAdminService'

const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:3001/ws'

interface IWsEvent {
  event: string
  data: Record<string, unknown>
}

interface IUseEventWebSocketOptions {
  eventId: number | null
  onEvent?: (event: IWsEvent) => void
}

export function useEventWebSocket({ eventId, onEvent }: IUseEventWebSocketOptions) {
  const [isConnected, setIsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onEventRef = useRef(onEvent)
  const eventIdRef = useRef(eventId)
  const isSubscribedRef = useRef(false)

  onEventRef.current = onEvent
  eventIdRef.current = eventId

  const cleanup = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
  }, [])

  const connect = useCallback(async () => {
    if (!eventIdRef.current) return

    try {
      const service = new EventAdminService()
      const { code } = await service.getWsSubscriptionCode(eventIdRef.current)

      const ws = new WebSocket(WS_URL)
      wsRef.current = ws

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'subscribe', code }))

        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, 25000)
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)

          if (message.type === 'subscribed') {
            setIsConnected(true)
            isSubscribedRef.current = true
          } else if (message.type === 'event' && isSubscribedRef.current) {
            onEventRef.current?.({ event: message.event, data: message.data })
          }
        } catch {
          // ignore parse errors
        }
      }

      ws.onclose = () => {
        setIsConnected(false)
        isSubscribedRef.current = false
        cleanup()

        reconnectTimeoutRef.current = setTimeout(() => {
          if (eventIdRef.current) {
            connect()
          }
        }, 3000)
      }

      ws.onerror = () => {
        ws.close()
      }
    } catch {
      reconnectTimeoutRef.current = setTimeout(() => {
        if (eventIdRef.current) {
          connect()
        }
      }, 5000)
    }
  }, [cleanup])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    cleanup()
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    setIsConnected(false)
    isSubscribedRef.current = false
  }, [cleanup])

  useEffect(() => {
    if (eventId) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [eventId])

  return { isConnected, disconnect }
}
